"use client"

import { useState } from "react"
import { useGoogleReCaptcha } from "@/lib/recaptcha-client"
import { motion } from "framer-motion"
import { X, Send, CheckCircle2, MessageSquare, Loader2 } from "lucide-react"
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
} from "@/components/ui/drawer"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Button } from "@/components/ui/button"
import { MultiSelectCombobox } from "@/components/ui/multi-select-combobox"
import { usePathname } from "next/navigation"
import { isValidLocale, type Locale } from "@/lib/i18n"
import { getTranslations } from "@/lib/translations"

export function QuoteFormSidebar() {
  const pathname = usePathname()
  
  // Get current locale from pathname
  const getCurrentLocale = (): Locale => {
    const segments = pathname.split('/').filter(Boolean)
    const firstSegment = segments[0]
    return isValidLocale(firstSegment) ? firstSegment : 'tr'
  }

  const currentLocale = getCurrentLocale()
  let t = getTranslations(currentLocale)
  
  // Safety check: if translations don't have quoteForm, try fallback to tr
  if (!t || !t.quoteForm) {
    console.error('QuoteForm translations not loaded for locale:', currentLocale, 't:', t, 't keys:', t ? Object.keys(t) : 't is null')
    // Try fallback to Turkish
    const trTranslations = getTranslations('tr')
    if (trTranslations && trTranslations.quoteForm) {
      // Use Turkish translations as fallback
      t = trTranslations
    } else {
      console.error('Fallback to tr also failed')
      return null
    }
  }
  
  const [isOpen, setIsOpen] = useState(false)
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    service: [] as string[],
    deadline: "",
    message: "",
  })
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState("")
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const { executeRecaptcha } = useGoogleReCaptcha()

  const serviceOptions = currentLocale === 'tr' ? [
    { value: "web-tasarim", label: "Web Tasarım" },
    { value: "web-yazilim", label: "Web Yazılım" },
    { value: "ui-ux-tasarim", label: "UI & UX Tasarım" },
    { value: "mobil-uygulama", label: "Mobil Uygulama" },
    { value: "masaustu-uygulama", label: "Masaüstü Uygulama" },
    { value: "e-ticaret", label: "E-ticaret Sistemleri" },
    { value: "shopify", label: "Shopify Tasarım - Geliştirme" },
    { value: "woocommerce", label: "WooCommerce Tasarım - Geliştirme" },
    { value: "otomasyon", label: "Otomasyon Çözümleri" },
    { value: "ai-servisler", label: "AI Ürünleştirilmiş Servisler" },
    { value: "diger", label: "Diğer" },
  ] : [
    { value: "web-tasarim", label: "Web Design" },
    { value: "web-yazilim", label: "Web Development" },
    { value: "ui-ux-tasarim", label: "UI & UX Design" },
    { value: "mobil-uygulama", label: "Mobile App" },
    { value: "masaustu-uygulama", label: "Desktop App" },
    { value: "e-ticaret", label: "E-Commerce Systems" },
    { value: "shopify", label: "Shopify Design - Development" },
    { value: "woocommerce", label: "WooCommerce Design - Development" },
    { value: "otomasyon", label: "Automation Solutions" },
    { value: "ai-servisler", label: "AI Productized Services" },
    { value: "diger", label: "Other" },
  ]

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitError("")
    setSubmitting(true)

    try {
      // Get reCAPTCHA token
      let recaptchaToken = ""
      if (executeRecaptcha) {
        recaptchaToken = await executeRecaptcha("quote_form")
      }

      const response = await fetch('/api/quote/request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          recaptchaToken,
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        setSubmitError(data.error || t.quoteForm.error)
        setSubmitting(false)
        return
      }

      setSubmitSuccess(true)
      // Reset form
    setFormData({
      name: "",
      company: "",
      email: "",
      phone: "",
        service: [],
      deadline: "",
      message: "",
    })

      // Close drawer and hide success message after 3 seconds
      setTimeout(() => {
    setIsOpen(false)
        setSubmitSuccess(false)
      }, 3000)
    } catch (error) {
      setSubmitError(t.quoteForm.error)
    } finally {
      setSubmitting(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  return (
    <>
      {/* Sticky Button */}
      <motion.button
        initial={{ opacity: 0, x: 100 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 1 }}
        onClick={() => setIsOpen(true)}
        className="fixed right-0 top-1/2 -translate-y-1/2 z-[70] bg-[rgb(3,74,166)]/70 hover:bg-[rgb(3,74,166)] text-white w-8 py-16 rounded-l-lg shadow-lg hover:shadow-xl transition-all duration-300 group backdrop-blur-sm flex items-center justify-center"
        aria-label={t.quoteForm.title}
      >
        <div className="text-sm font-semibold font-mono transform rotate-90 whitespace-nowrap origin-center" style={{ direction: 'ltr' }} suppressHydrationWarning>
          {t.quoteForm.title}
        </div>
      </motion.button>

      {/* Sidebar */}
      <Drawer open={isOpen} onOpenChange={setIsOpen} direction="right">
        <DrawerContent className="h-full w-full sm:max-w-lg">
          <DrawerHeader className="border-b">
            <div className="flex items-center justify-between">
              <div>
                <DrawerTitle className="text-2xl font-bold font-mono">{t.quoteForm.title}</DrawerTitle>
                <DrawerDescription>
                  {t.quoteForm.description}
                </DrawerDescription>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="rounded-md p-2 hover:bg-gray-100 transition-colors"
                aria-label={currentLocale === 'tr' ? 'Kapat' : 'Close'}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </DrawerHeader>

          <div className="flex-1 overflow-y-auto p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Kişisel Bilgiler */}
              <div className="space-y-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-[rgb(3,74,166)]/10 rounded-lg">
                    <CheckCircle2 className="h-5 w-5 text-[rgb(3,74,166)]" />
                  </div>
                  <h2 className="text-xl font-bold font-mono">{t.quoteForm.personalInfo}</h2>
                </div>
                <div className="grid grid-cols-1 gap-4">
                  <Input
                    type="text"
                    name="name"
                    placeholder={`${t.quoteForm.name} *`}
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="h-12"
                  />
                  <Input
                    type="text"
                    name="company"
                    placeholder={t.quoteForm.company}
                    value={formData.company}
                    onChange={handleChange}
                    className="h-12"
                  />
                  <Input
                    type="email"
                    name="email"
                    placeholder={`${t.quoteForm.email} *`}
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="h-12"
                  />
                  <Input
                    type="tel"
                    name="phone"
                    placeholder={`${t.quoteForm.phone} *`}
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="h-12"
                  />
                </div>
              </div>

              {/* Proje Detayları */}
              <div className="space-y-4 pt-6 border-t">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-[rgb(3,74,166)]/10 rounded-lg">
                    <CheckCircle2 className="h-5 w-5 text-[rgb(3,74,166)]" />
                  </div>
                  <h2 className="text-xl font-bold font-mono">{t.quoteForm.projectDetails}</h2>
                </div>
                <div className="grid grid-cols-1 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">{t.quoteForm.service} *</label>
                    <MultiSelectCombobox
                      options={serviceOptions}
                    value={formData.service}
                      onChange={(selected) => {
                        setFormData((prev) => ({ ...prev, service: selected }))
                      }}
                      placeholder={t.quoteForm.selectService}
                    required
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">{t.quoteForm.deadline}</label>
                  <select
                      name="deadline"
                      value={formData.deadline}
                    onChange={handleChange}
                    className="flex h-12 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                      <option value="">{t.quoteForm.selectDeadline}</option>
                      <option value="0-2-hafta">{t.quoteForm.deadlineOptions["0-2"]}</option>
                      <option value="2-4-hafta">{t.quoteForm.deadlineOptions["2-4"]}</option>
                      <option value="4-8-hafta">{t.quoteForm.deadlineOptions["4-8"]}</option>
                      <option value="8-10-hafta">{t.quoteForm.deadlineOptions["8-10"]}</option>
                      <option value="10-hafta-ustu">{t.quoteForm.deadlineOptions["10+"]}</option>
                  </select>
                  </div>
                </div>
              </div>

              {/* Mesaj */}
              <div className="space-y-4 pt-6 border-t">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-[rgb(3,74,166)]/10 rounded-lg">
                    <MessageSquare className="h-5 w-5 text-[rgb(3,74,166)]" />
                  </div>
                <h2 className="text-xl font-bold font-mono">{t.quoteForm.message}</h2>
                </div>
                <Textarea
                  name="message"
                  placeholder={`${currentLocale === 'tr' ? 'Projeniz hakkında detaylı bilgi veriniz...' : 'Please provide detailed information about your project...'} *`}
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  className="min-h-[150px]"
                />
              </div>

              {/* Error Message */}
              {submitError && (
                <div className="pt-4">
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                    {submitError}
                  </div>
                </div>
              )}

              {/* Success Message */}
              {submitSuccess && (
                <div className="pt-4">
                  <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
                    {t.quoteForm.success}
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <div className="pt-4">
                <Button
                  type="submit"
                  size="lg"
                  disabled={submitting}
                  className="w-full bg-[rgb(3,74,166)] hover:bg-[rgb(3,62,140)] text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      {t.quoteForm.sending}
                    </>
                  ) : (
                    <>
                  <Send className="mr-2 h-5 w-5" />
                      {t.quoteForm.send}
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </DrawerContent>
      </Drawer>
    </>
  )
}

